import React, { useState } from 'react';
import { db, auth } from '@archicore/shared';
import { doc, updateDoc, Timestamp } from 'firebase/firestore';
import { 
  X, Users, Briefcase, Calendar, Shield, 
  Trash2, AlertTriangle, ExternalLink, Activity,
  Save, Loader2, Power, PowerOff, Sparkles, Clock, CheckCircle2,
  HardDrive, Cpu, Gift, PlusCircle, CreditCard, Building2
} from 'lucide-react';

interface TenantDetailsModalProps {
  org: any;
  onClose: () => void;
  onUpdate: (updatedData: any) => void;
}

export default function TenantDetailsModal({ org, onClose, onUpdate }: TenantDetailsModalProps) {
  const [isDeleting, setIsDeleting] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [isTogglingStatus, setIsTogglingStatus] = useState(false);
  const [showPurgeModal, setShowPurgeModal] = useState(false);
  const [cancellationReason, setCancellationReason] = useState('Falta de pago / No renovación');
  const [deletionNotes, setDeletionNotes] = useState('');
  const [confirmSlugInput, setConfirmSlugInput] = useState('');

  // Local state for editable quotas & plan
  const [selectedPlan, setSelectedPlan] = useState<string>(org.plan || 'inicio');
  const [paymentMethod, setPaymentMethod] = useState<string>(org.paymentMethod || 'transfer');
  const [billingNotes, setBillingNotes] = useState<string>(org.billingNotes || '');
  const [excludeFromMRR, setExcludeFromMRR] = useState<boolean>(Boolean(org.excludeFromMRR ?? (org.isInternal || org.isCourtesy || org.id === 'bonnel' || org.name?.toLowerCase().includes('bonnel') || org.paymentMethod === 'courtesy' || org.paymentMethod === 'convenio' || org.isConvenio)));
  const [customMonthlyPrice, setCustomMonthlyPrice] = useState<string>(org.customMonthlyPriceMXN !== undefined && org.customMonthlyPriceMXN !== null ? String(org.customMonthlyPriceMXN) : '');

  // Quick action feedback states
  const [actionFeedback, setActionFeedback] = useState<{ message: string; type: 'success' | 'info' | 'error' } | null>(null);
  const [isApplyingBonus, setIsApplyingBonus] = useState(false);
  const [isExtendingTrial, setIsExtendingTrial] = useState(false);
  const [isApplyingConvenio, setIsApplyingConvenio] = useState(false);

  const PLAN_QUOTAS_MAP: Record<string, { usersLimit: number; projectsLimit: number; monthlyAiScansLimit: number; storageGB: number }> = {
    inicio: { usersLimit: 3, projectsLimit: 2, monthlyAiScansLimit: 500, storageGB: 5 },
    estudio: { usersLimit: 10, projectsLimit: 5, monthlyAiScansLimit: 1500, storageGB: 25 },
    constructora: { usersLimit: 30, projectsLimit: 15, monthlyAiScansLimit: 5000, storageGB: 100 },
    enterprise: { usersLimit: 999, projectsLimit: 999, monthlyAiScansLimit: 99999, storageGB: 500 },
    trial: { usersLimit: 3, projectsLimit: 2, monthlyAiScansLimit: 100, storageGB: 1 },
    free: { usersLimit: 3, projectsLimit: 2, monthlyAiScansLimit: 100, storageGB: 1 }
  };

  const [quotas, setQuotas] = useState({
    usersLimit: String(org.quotas?.usersLimit || PLAN_QUOTAS_MAP[org.plan]?.usersLimit || 10),
    projectsLimit: String(org.quotas?.projectsLimit || PLAN_QUOTAS_MAP[org.plan]?.projectsLimit || 5),
    monthlyAiScansLimit: String(org.quotas?.monthlyAiScansLimit || PLAN_QUOTAS_MAP[org.plan]?.monthlyAiScansLimit || 100)
  });
  const [saveSuccess, setSaveSuccess] = useState(false);

  // Local trial date tracking
  const [currentTrialEndsAt, setCurrentTrialEndsAt] = useState<Date | null>(() => {
    if (!org.trialEndsAt) return null;
    return org.trialEndsAt?.toDate ? org.trialEndsAt.toDate() : new Date(org.trialEndsAt);
  });

  const showFeedback = (message: string, type: 'success' | 'info' | 'error' = 'success') => {
    setActionFeedback({ message, type });
    setTimeout(() => setActionFeedback(null), 4000);
  };

  const handlePlanChange = (newPlan: string) => {
    setSelectedPlan(newPlan);
    if (PLAN_QUOTAS_MAP[newPlan]) {
      setQuotas({
        usersLimit: String(PLAN_QUOTAS_MAP[newPlan].usersLimit),
        projectsLimit: String(PLAN_QUOTAS_MAP[newPlan].projectsLimit),
        monthlyAiScansLimit: String(PLAN_QUOTAS_MAP[newPlan].monthlyAiScansLimit)
      });
    }
  };

  // 1-CLIC: Bonificar IA directamente
  const handleBonusAi = async (bonusAmount: number) => {
    setIsApplyingBonus(true);
    try {
      const currentLimit = Number(quotas.monthlyAiScansLimit) || 100;
      const newLimit = currentLimit + bonusAmount;
      
      const orgRef = doc(db, 'organizations', org.id);
      await updateDoc(orgRef, {
        'quotas.monthlyAiScansLimit': newLimit,
        'quotas.aiScansPerMonth': newLimit,
        updatedAt: new Date()
      });

      setQuotas(prev => ({ ...prev, monthlyAiScansLimit: String(newLimit) }));
      onUpdate({
        ...org,
        quotas: {
          ...org.quotas,
          monthlyAiScansLimit: newLimit,
          aiScansPerMonth: newLimit
        }
      });

      showFeedback(`🎁 ¡Se bonificaron +${bonusAmount} procesamientos de Core IA con éxito! (Total: ${newLimit})`);
    } catch (err: any) {
      console.error('Error bonificando IA:', err);
      showFeedback(`Error al bonificar IA: ${err.message}`, 'error');
    } finally {
      setIsApplyingBonus(false);
    }
  };

  // 1-CLIC: Extender días de prueba Trial
  const handleExtendTrial = async (daysToAdd: number) => {
    setIsExtendingTrial(true);
    try {
      const baseDate = currentTrialEndsAt && currentTrialEndsAt.getTime() > Date.now() 
        ? currentTrialEndsAt 
        : new Date();

      const newDate = new Date(baseDate.getTime() + daysToAdd * 24 * 60 * 60 * 1000);
      const newTimestamp = Timestamp.fromDate(newDate);

      const orgRef = doc(db, 'organizations', org.id);
      await updateDoc(orgRef, {
        trialEndsAt: newTimestamp,
        isTrial: true,
        updatedAt: new Date()
      });

      setCurrentTrialEndsAt(newDate);
      onUpdate({
        ...org,
        trialEndsAt: newTimestamp,
        isTrial: true
      });

      const formatted = newDate.toLocaleDateString('es-MX', { day: '2-digit', month: 'short', year: 'numeric' });
      showFeedback(`⏳ ¡Periodo de prueba extendido +${daysToAdd} días! Nueva fecha de vigencia: ${formatted}`);
    } catch (err: any) {
      console.error('Error extendiendo periodo de prueba:', err);
      showFeedback(`Error al extender prueba: ${err.message}`, 'error');
    } finally {
      setIsExtendingTrial(false);
    }
  };

  // 1-CLIC: Establecer Convenio Anual (1 Año Feedback)
  const handleSetConvenio = async () => {
    const confirmConvenio = window.confirm(
      `¿Establecer Convenio Anual de 1 Año para "${org.name}"?\n\n• Plan: Constructora (o el actual)\n• Costo: $0 MXN (Excluido de MRR)\n• Vigencia: 365 días a partir de hoy\n• Acceso garantizado sin corte de Trial`
    );
    if (!confirmConvenio) return;

    setIsApplyingConvenio(true);
    try {
      const oneYearFromNow = new Date(Date.now() + 365 * 24 * 60 * 60 * 1000);
      const newTimestamp = Timestamp.fromDate(oneYearFromNow);

      const targetPlan = selectedPlan === 'trial' || selectedPlan === 'free' ? 'constructora' : selectedPlan;
      const targetQuotas = PLAN_QUOTAS_MAP[targetPlan] || PLAN_QUOTAS_MAP['constructora'];

      const orgRef = doc(db, 'organizations', org.id);
      const updatePayload = {
        plan: targetPlan,
        isTrial: false,
        isConvenio: true,
        paymentMethod: 'convenio',
        excludeFromMRR: true,
        customMonthlyPriceMXN: 0,
        trialEndsAt: newTimestamp,
        billingNotes: org.billingNotes || 'Convenio anual sin costo a cambio de feedback',
        'quotas.usersLimit': Number(quotas.usersLimit) > targetQuotas.usersLimit ? Number(quotas.usersLimit) : targetQuotas.usersLimit,
        'quotas.projectsLimit': Number(quotas.projectsLimit) > targetQuotas.projectsLimit ? Number(quotas.projectsLimit) : targetQuotas.projectsLimit,
        'quotas.monthlyAiScansLimit': Number(quotas.monthlyAiScansLimit) > targetQuotas.monthlyAiScansLimit ? Number(quotas.monthlyAiScansLimit) : targetQuotas.monthlyAiScansLimit,
        updatedAt: new Date()
      };

      await updateDoc(orgRef, updatePayload);

      setSelectedPlan(targetPlan);
      setPaymentMethod('convenio');
      setExcludeFromMRR(true);
      setCustomMonthlyPrice('0');
      setCurrentTrialEndsAt(oneYearFromNow);

      onUpdate({
        ...org,
        plan: targetPlan,
        isTrial: false,
        isConvenio: true,
        paymentMethod: 'convenio',
        excludeFromMRR: true,
        customMonthlyPriceMXN: 0,
        trialEndsAt: newTimestamp,
        quotas: {
          ...org.quotas,
          usersLimit: Number(quotas.usersLimit) > targetQuotas.usersLimit ? Number(quotas.usersLimit) : targetQuotas.usersLimit,
          projectsLimit: Number(quotas.projectsLimit) > targetQuotas.projectsLimit ? Number(quotas.projectsLimit) : targetQuotas.projectsLimit,
          monthlyAiScansLimit: Number(quotas.monthlyAiScansLimit) > targetQuotas.monthlyAiScansLimit ? Number(quotas.monthlyAiScansLimit) : targetQuotas.monthlyAiScansLimit,
        }
      });

      const formatted = oneYearFromNow.toLocaleDateString('es-MX', { day: '2-digit', month: 'long', year: 'numeric' });
      showFeedback(`🤝 ¡Convenio Anual establecido con éxito! Vigencia hasta el ${formatted} y $0 en MRR.`);
    } catch (err: any) {
      console.error('Error estableciendo convenio anual:', err);
      showFeedback(`Error al establecer convenio: ${err.message}`, 'error');
    } finally {
      setIsApplyingConvenio(false);
    }
  };

  const handleSave = async () => {
    setIsSaving(true);
    setSaveSuccess(false);
    try {
      const orgRef = doc(db, 'organizations', org.id);
      const uLimit = Number(quotas.usersLimit);
      const pLimit = Number(quotas.projectsLimit);
      const aiLimit = Number(quotas.monthlyAiScansLimit);

      const isConvenioActive = paymentMethod === 'convenio' || Boolean(org.isConvenio);
      const isTrialPlan = (selectedPlan === 'trial' || selectedPlan === 'free') && !isConvenioActive;
      const customPriceNum = isConvenioActive ? 0 : (customMonthlyPrice.trim() !== '' && !isNaN(Number(customMonthlyPrice)) ? Number(customMonthlyPrice) : null);
      const shouldExcludeMRR = isConvenioActive ? true : excludeFromMRR;

      const updatePayload: any = {
        plan: selectedPlan,
        isTrial: isTrialPlan,
        isConvenio: isConvenioActive,
        paymentMethod,
        billingNotes,
        excludeFromMRR: shouldExcludeMRR,
        customMonthlyPriceMXN: customPriceNum,
        quotas: {
          ...org.quotas,
          usersLimit: uLimit,
          projectsLimit: pLimit,
          monthlyAiScansLimit: aiLimit,
          aiScansPerMonth: aiLimit
        },
        updatedAt: new Date()
      };

      await updateDoc(orgRef, updatePayload);
      
      onUpdate({ 
        ...org, 
        plan: selectedPlan,
        isTrial: isTrialPlan,
        isConvenio: isConvenioActive,
        paymentMethod,
        billingNotes,
        excludeFromMRR: shouldExcludeMRR,
        customMonthlyPriceMXN: customPriceNum,
        quotas: { 
          ...org.quotas, 
          usersLimit: uLimit, 
          projectsLimit: pLimit, 
          monthlyAiScansLimit: aiLimit, 
          aiScansPerMonth: aiLimit 
        } 
      });
      setSaveSuccess(true);
      showFeedback('Configuración y cuotas actualizadas correctamente.');
      
      setTimeout(() => setSaveSuccess(false), 3000);
    } catch (err: any) {
      console.error('Error saving quotas:', err);
      showFeedback('Error al guardar: ' + err.message, 'error');
    } finally {
      setIsSaving(false);
    }
  };

  const handleToggleStatus = async () => {
    const isCurrentlySuspended = org.status === 'suspended';
    const newStatus = isCurrentlySuspended ? 'active' : 'suspended';
    
    const confirmed = window.confirm(
      isCurrentlySuspended
        ? `¿Reactivar la organización "${org.name}"? Se restablecerán sus accesos.`
        : `¿Suspender la organización "${org.name}"? Iniciará el período de gracia de 30 días de resguardo.`
    );
    if (!confirmed) return;

    setIsTogglingStatus(true);
    try {
      const orgRef = doc(db, 'organizations', org.id);
      const suspendedDate = isCurrentlySuspended ? null : new Date();

      await updateDoc(orgRef, {
        status: newStatus,
        suspendedAt: suspendedDate,
        updatedAt: new Date()
      });

      onUpdate({ 
        ...org, 
        status: newStatus, 
        suspendedAt: suspendedDate 
      });

      showFeedback(`Organización ${newStatus === 'active' ? 'reactivada' : 'suspendida'} correctamente.`);
    } catch (err: any) {
      console.error('Error toggling status:', err);
      showFeedback('Error: ' + err.message, 'error');
    } finally {
      setIsTogglingStatus(false);
    }
  };

  const handleExecutePurge = async (e: React.FormEvent) => {
    e.preventDefault();
    if (confirmSlugInput.trim() !== org.id.trim()) {
      alert(`Para confirmar, debes escribir exactamente el ID: "${org.id}"`);
      return;
    }

    setIsDeleting(true);
    try {
      const idToken = await auth.currentUser?.getIdToken();
      const projectId = import.meta.env.VITE_FIREBASE_PROJECT_ID || 'control-de-asistencia-en-obra';
      const url = `https://us-central1-${projectId}.cloudfunctions.net/deleteOrganization`;
      
      const response = await fetch(url, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${idToken}`
        },
        body: JSON.stringify({ 
          targetOrgId: org.id,
          cancellationReason,
          notes: deletionNotes
        })
      });

      const resData = await response.json();
      if (!response.ok) throw new Error(resData.error || 'Error en el servidor al purgar la organización.');
      
      alert(`Organización purgada con éxito. Se guardó el registro histórico en 'organizations_history'.`);
      setShowPurgeModal(false);
      onUpdate(null);
      onClose();
    } catch (err: any) {
      console.error('Error purging organization:', err);
      alert('Error al purgar: ' + err.message);
    } finally {
      setIsDeleting(false);
    }
  };

  // Cálculos de Pilares y Límites
  const planInfo = PLAN_QUOTAS_MAP[selectedPlan] || PLAN_QUOTAS_MAP.inicio;
  const storageLimitGB = planInfo.storageGB;
  const aiUsed = Number(org.monthlyAiScansUsed || org.aiScansUsed || 0);
  const aiLimit = Number(quotas.monthlyAiScansLimit || 100);
  const aiPercentage = Math.round((aiUsed / aiLimit) * 100);

  const storageUsedMB = Number(org.storageUsedMB) || Math.min(Math.round((aiUsed * 0.15 + (org.projectsCount || 1) * 12) * 10) / 10, storageLimitGB * 1024);
  const storagePercentage = Math.round((storageUsedMB / (storageLimitGB * 1024)) * 100);

  const activeProjects = Number(org.projectsCount || 0);
  const archivedProjects = Number(org.archivedProjectsCount || 0);
  const projectsLimit = Number(quotas.projectsLimit || 1);

  const usersCount = Number(org.activeUsersCount || 1);
  const usersLimit = Number(quotas.usersLimit || 3);

  return (
    <div className="fixed inset-0 bg-slate-900/60 backdrop-blur-sm flex items-center justify-center z-50 p-4">
      <div className="bg-white border border-slate-200 rounded-[2rem] w-full max-w-3xl shadow-2xl relative overflow-hidden flex flex-col max-h-[92vh]">
        {/* Top Header Accent */}
        <div className="absolute top-0 left-0 w-full h-1.5 bg-gradient-to-r from-cyan-500 via-orange-500 to-indigo-600" />
        
        {/* Modal Header */}
        <div className="p-6 border-b border-slate-100 flex justify-between items-center bg-slate-50/50">
          <div className="flex items-center gap-4">
            <div className="p-3 bg-white rounded-2xl border border-slate-200 shadow-sm text-cyan-600">
              <Building2 size={24} />
            </div>
            <div>
              <div className="flex items-center gap-2">
                <h2 className="text-xl font-bold text-slate-900">{org.name}</h2>
                <span className={`px-2.5 py-0.5 rounded-full text-[10px] font-black uppercase tracking-wider ${
                  org.status === 'suspended' ? 'bg-red-100 text-red-700' : 'bg-emerald-100 text-emerald-700'
                }`}>
                  {org.status === 'suspended' ? 'Suspendido' : 'Activo'}
                </span>
                {(org.isTrial || selectedPlan === 'trial') && (
                  <span className="px-2.5 py-0.5 rounded-full text-[10px] font-black uppercase tracking-wider bg-amber-100 text-amber-800 flex items-center gap-1">
                    <Clock size={10} />
                    Trial
                  </span>
                )}
              </div>
              <p className="text-[10px] font-mono font-bold text-slate-400 uppercase tracking-tighter mt-0.5">
                ID: {org.id} • Creado por: {org.createdBy || 'Sistema'}
              </p>
            </div>
          </div>
          <button 
            onClick={onClose} 
            className="p-2 hover:bg-slate-100 rounded-xl transition-colors text-slate-400 hover:text-slate-900"
          >
            <X size={24} />
          </button>
        </div>

        {/* Action Feedback Toast */}
        {actionFeedback && (
          <div className={`mx-6 mt-4 p-3.5 rounded-xl border text-xs font-bold flex items-center justify-between animate-fadeIn ${
            actionFeedback.type === 'error' ? 'bg-red-50 text-red-700 border-red-200' :
            'bg-emerald-50 text-emerald-800 border-emerald-200'
          }`}>
            <div className="flex items-center gap-2">
              <CheckCircle2 size={16} className={actionFeedback.type === 'error' ? 'text-red-500' : 'text-emerald-600'} />
              <span>{actionFeedback.message}</span>
            </div>
            <button onClick={() => setActionFeedback(null)} className="text-slate-400 hover:text-slate-700">✕</button>
          </div>
        )}

        <div className="p-6 md:p-8 overflow-y-auto space-y-6 flex-1">

          {/* 1. SECCIÓN DE ACCIONES RÁPIDAS DE 1-CLIC */}
          <div className="bg-gradient-to-r from-slate-900 via-indigo-950 to-slate-900 p-5 rounded-2xl text-white shadow-md border border-indigo-900/40">
            <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-4 pb-3 border-b border-white/10">
              <div>
                <span className="text-[10px] font-black uppercase tracking-widest text-orange-400 flex items-center gap-1.5">
                  <Gift size={12} />
                  Acciones Comerciales de 1-Clic
                </span>
                <h3 className="text-sm font-bold text-white mt-0.5">Bonificaciones y Cortesías de Soporte</h3>
              </div>
              <span className="text-[11px] text-slate-300">
                Se aplican de forma inmediata en Firestore
              </span>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              {/* Bonificar IA */}
              <div className="bg-white/5 border border-white/10 rounded-xl p-3.5 flex flex-col justify-between">
                <div>
                  <div className="flex items-center justify-between mb-2">
                    <span className="text-xs font-bold text-purple-300 flex items-center gap-1.5">
                      <Cpu size={14} />
                      Bonificar Core IA
                    </span>
                    <span className="text-[10px] text-slate-400">Cuota actual: {quotas.monthlyAiScansLimit}</span>
                  </div>
                  <p className="text-[10px] text-slate-300 mb-2">Añade procesamientos IA inmediatos al ciclo actual.</p>
                </div>
                <div className="flex items-center gap-1.5">
                  <button
                    onClick={() => handleBonusAi(50)}
                    disabled={isApplyingBonus}
                    className="flex-1 py-1.5 bg-purple-600/60 hover:bg-purple-600 text-white rounded-lg text-xs font-bold transition-all border border-purple-500/40 disabled:opacity-50"
                  >
                    +50
                  </button>
                  <button
                    onClick={() => handleBonusAi(100)}
                    disabled={isApplyingBonus}
                    className="flex-1 py-1.5 bg-gradient-to-r from-purple-600 to-indigo-600 hover:opacity-90 text-white rounded-lg text-xs font-bold transition-all shadow-sm disabled:opacity-50"
                  >
                    +100
                  </button>
                  <button
                    onClick={() => handleBonusAi(500)}
                    disabled={isApplyingBonus}
                    className="flex-1 py-1.5 bg-purple-500/30 hover:bg-purple-500 text-purple-200 hover:text-white rounded-lg text-xs font-bold transition-all border border-purple-500/30 disabled:opacity-50"
                  >
                    +500
                  </button>
                </div>
              </div>

              {/* Extender Prueba */}
              <div className="bg-white/5 border border-white/10 rounded-xl p-3.5 flex flex-col justify-between">
                <div>
                  <div className="flex items-center justify-between mb-2">
                    <span className="text-xs font-bold text-amber-300 flex items-center gap-1.5">
                      <Clock size={14} />
                      Extender Periodo Trial
                    </span>
                    <span className="text-[10px] text-slate-400">
                      {currentTrialEndsAt 
                        ? `${currentTrialEndsAt.toLocaleDateString('es-MX', { day: '2-digit', month: 'short' })}`
                        : 'Sin límite'}
                    </span>
                  </div>
                  <p className="text-[10px] text-slate-300 mb-2">Amplía días de prueba sin alterar el plan asignado.</p>
                </div>
                <div className="flex items-center gap-1.5">
                  <button
                    onClick={() => handleExtendTrial(7)}
                    disabled={isExtendingTrial}
                    className="flex-1 py-1.5 bg-amber-600/60 hover:bg-amber-600 text-white rounded-lg text-xs font-bold transition-all border border-amber-500/40 disabled:opacity-50"
                  >
                    +7d
                  </button>
                  <button
                    onClick={() => handleExtendTrial(15)}
                    disabled={isExtendingTrial}
                    className="flex-1 py-1.5 bg-gradient-to-r from-amber-600 to-orange-600 hover:opacity-90 text-white rounded-lg text-xs font-bold transition-all shadow-sm disabled:opacity-50"
                  >
                    +15d
                  </button>
                  <button
                    onClick={() => handleExtendTrial(30)}
                    disabled={isExtendingTrial}
                    className="flex-1 py-1.5 bg-amber-500/30 hover:bg-amber-500 text-amber-200 hover:text-white rounded-lg text-xs font-bold transition-all border border-amber-500/30 disabled:opacity-50"
                  >
                    +30d
                  </button>
                </div>
              </div>

              {/* Convenio Anual (1 Año Feedback) */}
              <div className="bg-white/5 border border-indigo-500/40 rounded-xl p-3.5 flex flex-col justify-between">
                <div>
                  <div className="flex items-center justify-between mb-2">
                    <span className="text-xs font-bold text-indigo-300 flex items-center gap-1.5">
                      <Calendar size={14} />
                      Convenio Anual (1 Año)
                    </span>
                    <span className={`text-[9px] font-black uppercase px-1.5 py-0.5 rounded ${
                      paymentMethod === 'convenio' || org.isConvenio 
                        ? 'bg-indigo-500 text-white' 
                        : 'bg-white/10 text-slate-300'
                    }`}>
                      {paymentMethod === 'convenio' || org.isConvenio ? 'Activo' : 'Disponible'}
                    </span>
                  </div>
                  <p className="text-[10px] text-slate-300 mb-2">
                    Otorga 365 días, cuotas de Constructora y $0 MRR a cambio de feedback.
                  </p>
                </div>
                <button
                  type="button"
                  onClick={handleSetConvenio}
                  disabled={isApplyingConvenio}
                  className="w-full py-1.5 bg-gradient-to-r from-indigo-600 via-indigo-700 to-blue-600 hover:opacity-95 text-white rounded-lg text-xs font-bold transition-all shadow-sm disabled:opacity-50 flex items-center justify-center gap-1.5"
                >
                  {isApplyingConvenio ? <Loader2 size={13} className="animate-spin" /> : '🤝 Aplicar 1 Año Gratis'}
                </button>
              </div>
            </div>
          </div>

          {/* 2. MONITOREO DE LOS 4 PILARES EN TIEMPO REAL */}
          <div>
            <h3 className="text-xs font-black uppercase tracking-widest text-slate-400 mb-3 border-l-4 border-cyan-500 pl-3">
              Monitoreo de Consumo y Capacidad (4 Pilares)
            </h3>
            
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3.5">
              
              {/* Pilar 1: Obras Activas */}
              <div className="p-4 bg-slate-50 border border-slate-200 rounded-2xl space-y-2">
                <div className="flex items-center justify-between text-xs text-slate-500">
                  <span className="font-bold flex items-center gap-1.5 text-slate-700">
                    <Building2 size={15} className="text-cyan-600" />
                    Obras Activas
                  </span>
                  <span className="font-bold text-slate-400">{Math.round((activeProjects / projectsLimit) * 100)}%</span>
                </div>
                <div className="text-xl font-extrabold text-slate-900">
                  {activeProjects} <span className="text-xs font-normal text-slate-400">/ {projectsLimit}</span>
                </div>
                <div className="w-full bg-slate-200 rounded-full h-1.5 overflow-hidden">
                  <div 
                    className="h-full bg-cyan-500 rounded-full transition-all"
                    style={{ width: `${Math.min((activeProjects / projectsLimit) * 100, 100)}%` }}
                  />
                </div>
                <p className="text-[10px] text-slate-500 flex justify-between">
                  <span>{archivedProjects} archivada{archivedProjects === 1 ? '' : 's'}</span>
                  <span className="text-cyan-600 font-semibold">Oficina Central libre</span>
                </p>
              </div>

              {/* Pilar 2: Core IA */}
              <div className={`p-4 rounded-2xl border space-y-2 ${
                aiPercentage >= 100 ? 'bg-red-50/70 border-red-200' :
                aiPercentage >= 80 ? 'bg-amber-50/70 border-amber-200' :
                'bg-slate-50 border-slate-200'
              }`}>
                <div className="flex items-center justify-between text-xs">
                  <span className="font-bold flex items-center gap-1.5 text-purple-700">
                    <Cpu size={15} />
                    Core IA (Mes)
                  </span>
                  <span className={`font-bold ${aiPercentage >= 100 ? 'text-red-600' : aiPercentage >= 80 ? 'text-amber-600' : 'text-slate-400'}`}>
                    {aiPercentage}%
                  </span>
                </div>
                <div className="text-xl font-extrabold text-slate-900">
                  {aiUsed} <span className="text-xs font-normal text-slate-400">/ {aiLimit}</span>
                </div>
                <div className="w-full bg-slate-200 rounded-full h-1.5 overflow-hidden">
                  <div 
                    className={`h-full rounded-full transition-all ${
                      aiPercentage >= 100 ? 'bg-red-500' : aiPercentage >= 80 ? 'bg-amber-500' : 'bg-purple-500'
                    }`}
                    style={{ width: `${Math.min(aiPercentage, 100)}%` }}
                  />
                </div>
                <p className="text-[10px] text-slate-500">
                  {aiLimit - aiUsed > 0 ? `${aiLimit - aiUsed} procesamientos disp.` : 'Cuota mensual agotada'}
                </p>
              </div>

              {/* Pilar 3: Nube Cloud (Storage) */}
              <div className="p-4 bg-slate-50 border border-slate-200 rounded-2xl space-y-2">
                <div className="flex items-center justify-between text-xs text-slate-500">
                  <span className="font-bold flex items-center gap-1.5 text-emerald-700">
                    <HardDrive size={15} />
                    Nube Cloud
                  </span>
                  <span className="font-bold text-slate-400">{storagePercentage}%</span>
                </div>
                <div className="text-xl font-extrabold text-slate-900">
                  {storageUsedMB} <span className="text-xs font-normal text-slate-400">MB / {storageLimitGB} GB</span>
                </div>
                <div className="w-full bg-slate-200 rounded-full h-1.5 overflow-hidden">
                  <div 
                    className="h-full bg-emerald-500 rounded-full transition-all"
                    style={{ width: `${Math.min(storagePercentage, 100)}%` }}
                  />
                </div>
                <p className="text-[10px] text-slate-500">
                  Fotos, comprobantes y remitos
                </p>
              </div>

              {/* Pilar 4: Usuarios */}
              <div className="p-4 bg-slate-50 border border-slate-200 rounded-2xl space-y-2">
                <div className="flex items-center justify-between text-xs text-slate-500">
                  <span className="font-bold flex items-center gap-1.5 text-amber-700">
                    <Users size={15} />
                    Usuarios
                  </span>
                  <span className="font-bold text-slate-400">{Math.round((usersCount / usersLimit) * 100)}%</span>
                </div>
                <div className="text-xl font-extrabold text-slate-900">
                  {usersCount} <span className="text-xs font-normal text-slate-400">/ {usersLimit}</span>
                </div>
                <div className="w-full bg-slate-200 rounded-full h-1.5 overflow-hidden">
                  <div 
                    className="h-full bg-amber-500 rounded-full transition-all"
                    style={{ width: `${Math.min((usersCount / usersLimit) * 100, 100)}%` }}
                  />
                </div>
                <p className="text-[10px] text-slate-500">
                  Residentes y administradores
                </p>
              </div>

            </div>
          </div>

          {/* 3. CONFIGURACIÓN DE PLAN Y FACTURACIÓN */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div className="bg-slate-50 p-5 rounded-2xl border border-slate-200 space-y-4">
              <div className="flex items-center gap-2.5">
                <Calendar className="text-indigo-600" size={18} />
                <h3 className="text-xs font-bold uppercase tracking-widest text-slate-700">Plan Contratado y Cobranza</h3>
              </div>

              <div className="space-y-3">
                <div>
                  <label className="text-[10px] font-bold text-slate-400 uppercase tracking-wider block mb-1">Plan Oficial</label>
                  <select
                    value={selectedPlan}
                    onChange={(e) => handlePlanChange(e.target.value)}
                    className="w-full bg-white border border-slate-200 text-slate-900 font-bold text-sm rounded-xl px-3 py-2.5 focus:ring-2 focus:ring-cyan-500 focus:outline-none capitalize"
                  >
                    <option value="inicio">Plan Inicio ($490 MXN • 3 us / 2 obras / 500 IA)</option>
                    <option value="estudio">Plan Estudio ($890 MXN • 10 us / 5 obras / 1,500 IA)</option>
                    <option value="constructora">Plan Constructora ($1,690 MXN • 30 us / 15 obras / 5,000 IA)</option>
                    <option value="enterprise">Plan a Medida / Corporativo (Personalizado)</option>
                    <option value="trial">Periodo de Prueba (30 Días • 3 us / 2 obras / 100 IA)</option>
                  </select>
                </div>

                <div className="grid grid-cols-2 gap-2.5">
                  <div>
                    <label className="text-[10px] font-bold text-slate-400 uppercase tracking-wider block mb-1">Método de Pago</label>
                    <select
                      value={paymentMethod}
                      onChange={(e) => setPaymentMethod(e.target.value)}
                      className="w-full bg-white border border-slate-200 text-slate-800 text-xs font-semibold rounded-xl px-2.5 py-2 focus:outline-none"
                    >
                      <option value="transfer">Transferencia (SPEI)</option>
                      <option value="card">Tarjeta de Crédito</option>
                      <option value="convenio">Convenio Anual (1 Año Feedback)</option>
                      <option value="courtesy">Cortesía / Demo</option>
                    </select>
                  </div>

                  <div>
                    <label className="text-[10px] font-bold text-slate-400 uppercase tracking-wider block mb-1">Notas Facturación</label>
                    <input
                      type="text"
                      placeholder="Ej. Factura #289 SPEI"
                      value={billingNotes}
                      onChange={(e) => setBillingNotes(e.target.value)}
                      className="w-full bg-white border border-slate-200 text-slate-800 text-xs font-medium rounded-xl px-2.5 py-2 focus:outline-none"
                    />
                  </div>
                </div>

                {/* Importe Mensual Acordado / Custom Price */}
                <div>
                  <label className="text-[10px] font-bold text-slate-400 uppercase tracking-wider block mb-1 flex items-center justify-between">
                    <span>Importe Mensual Acordado ($ MXN)</span>
                    <span className="text-[9px] font-normal text-slate-400 lowercase">opcional para plan a medida</span>
                  </label>
                  <div className="relative">
                    <span className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400 text-xs font-bold">$</span>
                    <input
                      type="number"
                      placeholder={`Por defecto según plan: $${selectedPlan === 'inicio' ? '490' : selectedPlan === 'estudio' ? '890' : selectedPlan === 'constructora' ? '1,690' : '4,990'} MXN`}
                      value={customMonthlyPrice}
                      onChange={(e) => setCustomMonthlyPrice(e.target.value)}
                      className="w-full bg-white border border-slate-200 text-slate-900 font-bold text-xs rounded-xl pl-7 pr-3 py-2 focus:ring-2 focus:ring-cyan-500 focus:outline-none placeholder:font-normal placeholder:text-slate-400"
                    />
                  </div>
                </div>

                {/* Switch: Computar en MRR */}
                <div className="pt-2 border-t border-slate-200/60">
                  <div className="flex items-center justify-between p-2.5 bg-white border border-slate-200 rounded-xl">
                    <div className="space-y-0.5">
                      <span className="text-xs font-bold text-slate-800 flex items-center gap-1.5">
                        💰 Computar en Ingresos MRR
                      </span>
                      <p className="text-[10px] text-slate-500 max-w-xs">
                        {excludeFromMRR 
                          ? '❌ Excluida del MRR (Empresa propia / Cortesía)' 
                          : '✅ Suma al MRR mensual y ARR anual'}
                      </p>
                    </div>
                    <button
                      type="button"
                      onClick={() => setExcludeFromMRR(!excludeFromMRR)}
                      className={`px-3 py-1.5 rounded-lg text-[11px] font-bold transition-all flex items-center gap-1.5 ${
                        excludeFromMRR 
                          ? 'bg-slate-100 text-slate-600 border border-slate-300 hover:bg-slate-200' 
                          : 'bg-emerald-600 text-white shadow-xs hover:bg-emerald-700'
                      }`}
                    >
                      {excludeFromMRR ? '🚫 Excluida del MRR' : '✓ Sumar a MRR'}
                    </button>
                  </div>
                </div>
              </div>
            </div>

            {/* Fechas Clave y Auditoría */}
            <div className="bg-slate-50 p-5 rounded-2xl border border-slate-200 space-y-3">
              <div className="flex items-center gap-2.5">
                <Activity className="text-emerald-600" size={18} />
                <h3 className="text-xs font-bold uppercase tracking-widest text-slate-700">Fechas Clave del Tenant</h3>
              </div>

              {(() => {
                const created = org.createdAt?.toDate ? org.createdAt.toDate() : (org.createdAt ? new Date(org.createdAt) : null);
                const trialEnd = currentTrialEndsAt;
                const daysAgo = created ? Math.floor((Date.now() - created.getTime()) / (1000 * 60 * 60 * 24)) : null;
                const daysLeft = trialEnd ? Math.ceil((trialEnd.getTime() - Date.now()) / (1000 * 60 * 60 * 24)) : null;
                const isConvenioActive = paymentMethod === 'convenio' || Boolean(org.isConvenio);
                
                return (
                  <div className="space-y-2.5 text-xs">
                    <div className="flex justify-between items-center py-1 border-b border-slate-200/60">
                      <span className="text-slate-500 font-medium">Fecha de Alta:</span>
                      <span className="font-bold text-slate-800">
                        {created ? `${created.toLocaleDateString('es-MX', { day: '2-digit', month: 'long', year: 'numeric' })}` : '—'}
                        {daysAgo !== null && <span className="text-slate-400 font-normal ml-1.5">(hace {daysAgo}d)</span>}
                      </span>
                    </div>

                    {isConvenioActive ? (
                      <div className="flex justify-between items-center py-1.5 px-2 bg-indigo-50/80 border border-indigo-200/70 rounded-xl">
                        <span className="text-indigo-700 font-bold flex items-center gap-1.5">
                          <Calendar size={13} className="text-indigo-600" />
                          Vigencia Convenio (1 Año):
                        </span>
                        <span className="font-bold text-indigo-950 text-right">
                          {trialEnd ? `${trialEnd.toLocaleDateString('es-MX', { day: '2-digit', month: 'long', year: 'numeric' })}` : 'Convenio 365 días'}
                          {daysLeft !== null && (
                            <span className="font-semibold text-indigo-700 ml-1.5">
                              ({daysLeft > 0 ? `${daysLeft} días restantes` : 'Convenio vencido'})
                            </span>
                          )}
                        </span>
                      </div>
                    ) : (
                      <div className="flex justify-between items-center py-1 border-b border-slate-200/60">
                        <span className="text-slate-500 font-medium">Vigencia Trial:</span>
                        <span className={`font-bold ${
                          daysLeft !== null && daysLeft <= 3 ? 'text-red-600' :
                          daysLeft !== null && daysLeft <= 7 ? 'text-amber-600' : 'text-slate-800'
                        }`}>
                          {trialEnd ? `${trialEnd.toLocaleDateString('es-MX', { day: '2-digit', month: 'long', year: 'numeric' })}` : 'Ilimitado / Sin vencimiento'}
                          {daysLeft !== null && (
                            <span className="font-semibold ml-1.5">
                              ({daysLeft > 0 ? `${daysLeft} días restantes` : 'Vencido'})
                            </span>
                          )}
                        </span>
                      </div>
                    )}

                    <div className="flex justify-between items-center py-1">
                      <span className="text-slate-500 font-medium">Límite de Almacenamiento:</span>
                      <span className="font-bold text-slate-800">{storageLimitGB} GB incluidos</span>
                    </div>
                  </div>
                );
              })()}
            </div>
          </div>

          {/* Ajuste Fino de Cuotas Manuales */}
          <div className="bg-slate-50 p-5 rounded-2xl border border-slate-200 space-y-3">
            <h4 className="text-xs font-bold uppercase tracking-wider text-slate-500">Ajuste Fino de Cuotas Manuales</h4>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
              <div>
                <label className="text-[10px] font-bold text-slate-500 uppercase block mb-1">Usuarios Límite</label>
                <input
                  type="number"
                  value={quotas.usersLimit}
                  onChange={(e) => setQuotas({ ...quotas, usersLimit: e.target.value })}
                  className="w-full px-3 py-2 bg-white border border-slate-200 rounded-xl text-xs font-bold text-slate-800 focus:outline-none"
                />
              </div>
              <div>
                <label className="text-[10px] font-bold text-slate-500 uppercase block mb-1">Obras Activas Límite</label>
                <input
                  type="number"
                  value={quotas.projectsLimit}
                  onChange={(e) => setQuotas({ ...quotas, projectsLimit: e.target.value })}
                  className="w-full px-3 py-2 bg-white border border-slate-200 rounded-xl text-xs font-bold text-slate-800 focus:outline-none"
                />
              </div>
              <div>
                <label className="text-[10px] font-bold text-slate-500 uppercase block mb-1">Core IA Procesamientos/Mes</label>
                <input
                  type="number"
                  value={quotas.monthlyAiScansLimit}
                  onChange={(e) => setQuotas({ ...quotas, monthlyAiScansLimit: e.target.value })}
                  className="w-full px-3 py-2 bg-white border border-slate-200 rounded-xl text-xs font-bold text-purple-800 focus:outline-none"
                />
              </div>
            </div>
          </div>

          {/* Soft Delete & Grace Period Banner (if suspended) */}
          {org.status === 'suspended' && (
            (() => {
              const suspendedDate = org.suspendedAt?.toDate ? org.suspendedAt.toDate() : (org.suspendedAt ? new Date(org.suspendedAt) : null);
              const daysSuspended = suspendedDate ? Math.floor((Date.now() - suspendedDate.getTime()) / (1000 * 60 * 60 * 24)) : 0;
              const daysRemaining = Math.max(0, 30 - daysSuspended);
              const isGraceExpired = daysSuspended >= 30;

              return (
                <div className={`p-5 rounded-2xl border ${isGraceExpired ? 'bg-red-50/80 border-red-200' : 'bg-amber-50/80 border-amber-200'} space-y-2`}>
                  <div className="flex items-center gap-3">
                    <div className={`p-2 rounded-xl ${isGraceExpired ? 'bg-red-100 text-red-700' : 'bg-amber-100 text-amber-700'}`}>
                      <Clock size={20} />
                    </div>
                    <div>
                      <h4 className={`text-sm font-bold ${isGraceExpired ? 'text-red-900' : 'text-amber-900'}`}>
                        {isGraceExpired ? '⚠️ Plazo de Gracia Vencido (Listo para Purga)' : '🟡 Período de Gracia de Retención Activo (30 días)'}
                      </h4>
                      <p className={`text-xs ${isGraceExpired ? 'text-red-700' : 'text-amber-700'}`}>
                        {suspendedDate ? `Suspendido el ${suspendedDate.toLocaleDateString('es-MX', { day: '2-digit', month: 'long', year: 'numeric' })}` : 'Suspendido recientemente'} • 
                        <strong className="ml-1">{daysSuspended} días transcurridos</strong> • 
                        {isGraceExpired ? <strong className="ml-1 text-red-700">Plazo de 30 días cumplido</strong> : <strong className="ml-1">{daysRemaining} días de resguardo restantes</strong>}
                      </p>
                    </div>
                  </div>
                  <p className="text-[11px] text-slate-600 pl-11">
                    {isGraceExpired 
                      ? 'El período de 30 días de resguardo ha concluido. Puedes reactivar al cliente si reanudó contrato o ejecutar la purga definitiva.'
                      : 'Durante este período toda la información del cliente está 100% resguardada. Puedes reactivarlo con un clic si regulariza su situación.'}
                  </p>
                </div>
              );
            })()
          )}

          {/* Zona de Operaciones de Riesgo y Suspensión */}
          <div className="p-5 bg-red-50 rounded-2xl border border-red-100 space-y-3">
            <h3 className="text-xs font-bold text-red-600 flex items-center gap-2 uppercase tracking-widest">
              <AlertTriangle size={14} />
              Zona de Operaciones de Riesgo
            </h3>
            <div className="flex flex-wrap gap-3">
              <button 
                onClick={handleToggleStatus}
                disabled={isTogglingStatus || isDeleting}
                className={`px-4 py-2 bg-white border ${org.status === 'suspended' ? 'border-green-200 text-green-700 hover:bg-green-600 hover:text-white' : 'border-amber-200 text-amber-700 hover:bg-amber-600 hover:text-white'} rounded-xl transition-all text-xs font-bold shadow-xs flex items-center gap-2 disabled:opacity-50`}
              >
                {isTogglingStatus ? <Loader2 className="animate-spin" size={14} /> : (org.status === 'suspended' ? <Power size={14} /> : <PowerOff size={14} />)}
                {isTogglingStatus ? 'Procesando...' : (org.status === 'suspended' ? 'Reactivar Cliente Inmediatamente' : 'Suspender Temporalmente (Iniciar Gracia)')}
              </button>

              <button 
                onClick={() => {
                  setConfirmSlugInput('');
                  setShowPurgeModal(true);
                }}
                disabled={isDeleting || isTogglingStatus}
                className="px-4 py-2 bg-white border border-red-200 text-red-600 rounded-xl hover:bg-red-600 hover:text-white transition-all text-xs font-bold shadow-xs flex items-center gap-2 disabled:opacity-50"
              >
                <Trash2 size={14} />
                <span>Purgar y Archivar Definitivamente</span>
              </button>
            </div>
          </div>
        </div>

        {/* Modal Footer */}
        <div className="p-6 border-t border-slate-100 bg-slate-50/50 flex justify-end gap-3">
          <button 
            onClick={onClose} 
            disabled={isSaving || isDeleting}
            className="px-5 py-2.5 rounded-xl text-slate-500 font-bold hover:bg-slate-100 transition-all text-xs disabled:opacity-50"
          >
            Cerrar
          </button>
          <button 
            onClick={handleSave}
            disabled={isSaving || isDeleting}
            className={`px-6 py-2.5 rounded-xl text-xs font-bold transition-all shadow-md flex items-center gap-2 disabled:opacity-50 ${
              saveSuccess ? 'bg-green-600 text-white' : 'bg-slate-900 text-white hover:bg-slate-800'
            }`}
          >
            {isSaving ? <Loader2 className="animate-spin" size={16} /> : (saveSuccess ? <CheckCircle2 size={16} /> : <Save size={16} />)}
            {isSaving ? 'Guardando...' : (saveSuccess ? '¡Guardado con Éxito!' : 'Guardar Cambios')}
          </button>
        </div>

        {/* Purge & Archive Confirmation Modal */}
        {showPurgeModal && (
          <div className="fixed inset-0 bg-slate-950/70 backdrop-blur-sm flex items-center justify-center z-50 p-4">
            <div className="bg-white border border-red-200 rounded-3xl max-w-lg w-full p-7 shadow-2xl space-y-5 relative overflow-hidden">
              <div className="absolute top-0 left-0 w-full h-1.5 bg-gradient-to-r from-red-500 via-rose-600 to-amber-500" />
              
              <div className="flex items-center gap-3 text-red-600">
                <div className="p-3 bg-red-50 rounded-2xl border border-red-100">
                  <Trash2 size={24} />
                </div>
                <div>
                  <h3 className="text-lg font-bold text-slate-900">Purga Total y Archivo Histórico</h3>
                  <p className="text-xs text-slate-500">Eliminación completa de recursos en Firebase</p>
                </div>
              </div>

              <form onSubmit={handleExecutePurge} className="space-y-4 text-xs">
                <div className="p-3.5 bg-slate-50 border border-slate-200 rounded-xl space-y-1 text-slate-700">
                  <p><strong>Organización:</strong> {org.name} <span className="font-mono text-slate-400">({org.id})</span></p>
                  <p><strong>Plan actual:</strong> <span className="uppercase font-bold text-slate-900">{org.plan}</span></p>
                </div>

                <div className="space-y-1.5">
                  <label className="block text-[10px] font-bold uppercase tracking-wider text-slate-500">
                    Motivo de Cancelación / Salida
                  </label>
                  <select
                    value={cancellationReason}
                    onChange={(e) => setCancellationReason(e.target.value)}
                    className="w-full px-3 py-2 bg-slate-50 border border-slate-200 rounded-xl text-xs font-semibold text-slate-900 focus:outline-none"
                  >
                    <option value="Falta de pago / No renovación">Falta de pago / No renovación</option>
                    <option value="Fin de proyecto / Temporal">Fin de proyecto / Uso temporal concluido</option>
                    <option value="Decisión del cliente">Decisión del cliente / Cambio de proveedor</option>
                    <option value="Trial / Prueba gratuita no convertida">Trial / Prueba gratuita no convertida</option>
                    <option value="Duplicado o prueba interna">Duplicado o prueba interna</option>
                    <option value="Otro motivo">Otro motivo</option>
                  </select>
                </div>

                <div className="space-y-1.5">
                  <label className="block text-[10px] font-bold uppercase tracking-wider text-slate-500">
                    Notas y Observaciones (Para el Historial Comercial)
                  </label>
                  <textarea
                    rows={2}
                    value={deletionNotes}
                    onChange={(e) => setDeletionNotes(e.target.value)}
                    placeholder="Ej. Comentaron que regresarán a fin de año."
                    className="w-full px-3 py-2 bg-slate-50 border border-slate-200 rounded-xl text-xs text-slate-900 focus:outline-none"
                  />
                </div>

                <div className="p-3 bg-red-50 border border-red-200 rounded-xl text-[11px] text-red-800 space-y-1">
                  <p className="font-bold flex items-center gap-1.5">
                    <AlertTriangle size={14} className="text-red-600" />
                    Esta acción ejecutará:
                  </p>
                  <ul className="list-disc list-inside space-y-0.5 text-[10px] text-red-700 pl-1">
                    <li>Registro permanente en <strong>organizations_history</strong>.</li>
                    <li>Eliminación de usuarios en <strong>Firebase Authentication</strong>.</li>
                    <li>Eliminación de fotos en <strong>Firebase Storage</strong>.</li>
                    <li>Eliminación de colecciones en <strong>Firestore</strong>.</li>
                  </ul>
                </div>

                <div className="space-y-1.5 pt-1">
                  <label className="block text-[10px] font-bold uppercase tracking-wider text-slate-500">
                    Escribe <strong className="font-mono text-slate-900 select-all">{org.id}</strong> para confirmar:
                  </label>
                  <input
                    type="text"
                    value={confirmSlugInput}
                    onChange={(e) => setConfirmSlugInput(e.target.value)}
                    placeholder={org.id}
                    className="w-full px-3 py-2 bg-slate-50 border border-slate-200 rounded-xl text-xs font-mono font-bold text-slate-900 focus:outline-none"
                    required
                  />
                </div>

                <div className="flex justify-end gap-3 pt-3 border-t border-slate-100">
                  <button
                    type="button"
                    disabled={isDeleting}
                    onClick={() => setShowPurgeModal(false)}
                    className="px-4 py-2 bg-slate-100 hover:bg-slate-200 text-slate-700 rounded-xl text-xs font-bold transition-colors disabled:opacity-50"
                  >
                    Cancelar
                  </button>
                  <button
                    type="submit"
                    disabled={isDeleting || confirmSlugInput.trim() !== org.id.trim()}
                    className="px-5 py-2 bg-red-600 hover:bg-red-700 text-white rounded-xl text-xs font-bold shadow-md transition-all flex items-center gap-2 disabled:opacity-40"
                  >
                    {isDeleting ? <Loader2 className="animate-spin" size={14} /> : <Trash2 size={14} />}
                    <span>{isDeleting ? 'Purgando Todo...' : 'Confirmar Purga Definitiva'}</span>
                  </button>
                </div>
              </form>
            </div>
          </div>
        )}

      </div>
    </div>
  );
}
